<script async setup>
import { defineProps, ref, onMounted, onBeforeUnmount, nextTick } from "vue";
import { tns } from "tiny-slider";

const props = defineProps({
    elementId: {
        type: [Number, String],
        required: true,
    },
    autoplay: {
        type: Boolean,
        default: false
    },
});

const sliderApi = ref(null);
const slideCount = ref(0);
const autoplayTimer = ref(null);

const stopAutoplay = () => {
    if (autoplayTimer.value) {
        clearInterval(autoplayTimer.value);
        autoplayTimer.value = null;
    }
};

const goToIndex = (index) => {
    sliderApi.value?.goTo(index);
};

const startAutoplay = () => {
    if (!props.autoplay) return;
    stopAutoplay();
    autoplayTimer.value = setInterval(() => {
        const current = sliderApi.value?.getInfo().index ?? 0;
        const target = current >= slideCount.value - 1 ? 0 : current + 1;
        goToIndex(target);
    }, 6000);
};

const prevSlide = () => {
    if (!sliderApi.value) return;
    const current = sliderApi.value.getInfo().index;
    const target = current <= 0 ? slideCount.value - 1 : current - 1;
    goToIndex(target);
    if (props.autoplay) startAutoplay();
};

const nextSlide = () => {
    if (!sliderApi.value) return;
    const current = sliderApi.value.getInfo().index;
    const target = current >= slideCount.value - 1 ? 0 : current + 1;
    goToIndex(target);
    if (props.autoplay) startAutoplay();
};

onMounted(async () => {
    await nextTick();

    const container = document.getElementById(`slider-${props.elementId}`);
    slideCount.value = container?.querySelectorAll(":scope > div").length ?? 0;

    sliderApi.value = tns({
        container: `#slider-${props.elementId}`,
        items: 1,
        slideBy: 1,
        gutter: 0,
        autoplay: false,
        autoplayButtonOutput: false,
        controls: false,
        nav: false,
        mouseDrag: false,
        speed: 400,
        loop: false,
        rewind: false,
        center: false,
    });

    // Hover Pause
    if (props.autoplay && container) {
        container.addEventListener("mouseenter", stopAutoplay);
        container.addEventListener("mouseleave", startAutoplay);
    }

    startAutoplay();
});

onBeforeUnmount(() => {
    stopAutoplay();
    sliderApi.value?.destroy();
    sliderApi.value = null;
});
</script>

<template>
    <div class="relative">
        <div class="overflow-hidden">
            <div :id="'slider-' + props.elementId">
                <slot />
            </div>
        </div>
        <div v-if="slideCount > 1" class="max-w-ts-content mx-auto relative">
            <div class="absolute bottom-5 right-4">
                <div class="flex gap-x-4">
                    <button class="cursor-pointer" @click="prevSlide">
                        <kd-icon-arrow-left class="drop-shadow-ts-tight" fill="gray" size="30" />
                    </button>
                    <button class="cursor-pointer" @click="nextSlide">
                        <kd-icon-arrow-right class="drop-shadow-ts-tight" fill="gray" size="30" />
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>
